import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {
  CountdownUnitProps,
  CountdownUnit as DefaultCountdownUnitComponent,
} from './CountdownUnit';
import {useCountdown} from './useCountdown';

export type {CountdownUnitProps};

export type CountdownContainerProps<ExtraProps = {}> = ExtraProps & {
  hide?: boolean;
  'data-testid': string;
};

type CountdownUnitPropsWithChildren =
  React.PropsWithChildren<CountdownContainerProps>;

export type CountdownSize = 'normal' | 'small';

export type CountdownProps<ExtraProps = {}> = ExtraProps & {
  className?: string;
  endDate: Date;
  onTimeout?: () => void;
  unitComponent?: React.ComponentType<
    React.PropsWithChildren<CountdownUnitProps>
  >;
  containerComponent?: React.ComponentType<CountdownUnitPropsWithChildren>;
  labelComponentStart?: React.ComponentType<CountdownUnitPropsWithChildren>;
  labelComponentEnd?: React.ComponentType<CountdownUnitPropsWithChildren>;
  size?: CountdownSize;
};

const DefaultContainer = styled.div<CountdownContainerProps>`
  display: flex;
  visibility: ${({hide}) => (hide ? 'hidden' : 'visible')};
  width: 85%;
  justify-content: space-around;
  padding: 10px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  box-sizing: border-box;
`;

export function Countdown<ExtraProps>({
  endDate,
  onTimeout,
  unitComponent: Unit = DefaultCountdownUnitComponent,
  containerComponent: Container = DefaultContainer,
  labelComponentStart: LabelComponentStart,
  labelComponentEnd: LabelComponentEnd,
  size,
  ...rest
}: CountdownProps<ExtraProps>) {
  const countdown = useCountdown(endDate);
  const [days, hours, minutes, seconds] = countdown;

  useEffect(() => {
    if (countdown.remainingTime !== undefined && countdown.remainingTime <= 0) {
      onTimeout?.();
    }
  }, [countdown.remainingTime]);

  return (
    <Container
      hide={countdown.hideCountdown || false}
      data-testid="countdown"
      {...rest}
    >
      {LabelComponentStart && (
        <LabelComponentStart data-testid="countdownLabelStart" />
      )}
      <Unit data-testid="countdownDays" value={days} unit="days" size={size} />
      <Unit
        data-testid="countdownHours"
        value={hours}
        unit="hours"
        size={size}
      />
      <Unit
        data-testid="countdownMinutes"
        value={minutes}
        unit="minutes"
        size={size}
      />
      <Unit
        data-testid="countdownSeconds"
        value={seconds}
        unit="seconds"
        size={size}
      />
      {LabelComponentEnd && (
        <LabelComponentEnd data-testid="countdownLabelEnd" />
      )}
    </Container>
  );
}
